import React, { forwardRef, useEffect, useRef, useState } from 'react';
import Icon from '../../components/Icon/Icon';
import classes from './CollapsibleCard.module.scss';
import {
  CollapsibleCardComponentProps,
  CollapsibleCardHeaderProps,
  CollapsibleCardProps,
} from './CollapsibleCard.types';

export const CollapsibleCardRoot = forwardRef<HTMLDivElement, CollapsibleCardProps>(
  ({ className, children, open, onOpenChange, isDefaultOpen, ...props }, ref) => {
    const [isExtended, setIsExtended] = useState(
      isDefaultOpen !== undefined ? isDefaultOpen && (open ?? true) : false
    );
    const hasMounted = useRef(false);
    // Open the card initially if the open prop is set
    useEffect(() => {
      if (open !== undefined) {
        setIsExtended(open);
      }
    }, [open]);

    // Open the card if the open prop changes
    useEffect(() => {
      if (hasMounted.current && open !== undefined) {
        setIsExtended(open);
        if (onOpenChange) {
          onOpenChange(open);
        }
      } else {
        hasMounted.current = true;
      }
    }, [open]);

    function toggleContentVisibility() {
      // Do not change the state if the open prop is set
      if (open === undefined) {
        setIsExtended((prev) => {
          const newState = !prev;
          if (onOpenChange) {
            onOpenChange(newState);
          }
          return newState;
        });
      } else if (onOpenChange) {
        onOpenChange(!open);
      }
    }

    return (
      <div
        ref={ref}
        className={[classes['card-root'], className].join(' ')}
        data-testid='collapsible-card-root'
        {...props}
      >
        {React.Children.map(children, (child) => {
          if (React.isValidElement<CollapsibleCardComponentProps>(child)) {
            return React.cloneElement(child, {
              isVisible: isExtended,
              toggleContentVisibility,
            });
          }
          return child;
        })}
      </div>
    );
  }
);

export const CollapsibleCardHeader = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> &
    CollapsibleCardHeaderProps &
    CollapsibleCardComponentProps
>(({ title, children, isVisible, toggleContentVisibility, ...props }, ref) => {
  //   const { ...restProps } = props; // Filter out toggleContentVisibility
  return (
    <div ref={ref} data-testid='collapsible-card-header' {...props}>
      <div className={'sui-flex sui-items-center sui-gap-0.5 sui-flex-row'}>
        <Icon
          onClick={toggleContentVisibility}
          data-testid={'collapsible-card-toggle'}
          name={'keyboard_arrow_down'}
          className={`${classes['toggle-icon']} ${isVisible ? classes['toggle-open'] : classes['toggle-closed']}`}
          size='s'
          sentiment='neutral'
          filled={false}
        />
        {children
          ? children
          : title && (
              <div style={{ color: '#191919', fontSize: '16px', fontWeight: '700' }}>
                {title}
              </div>
            )}
      </div>
    </div>
  );
});

export const CollapsibleCardContent = forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement> & CollapsibleCardComponentProps
>(({ children, isVisible, toggleContentVisibility, ...props }, ref) => {
  return (
    <div
      className={`${classes['card-content']} ${isVisible ? classes['content-visible'] : classes['content-hidden']} `}
      ref={ref}
      data-testid='collapsible-card-content'
      {...props}
    >
      {children}
    </div>
  );
});

export const CollapsibleCard = {
  Root: CollapsibleCardRoot,
  Header: CollapsibleCardHeader,
  Content: CollapsibleCardContent,
};

export default CollapsibleCard;
